'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { CheckCircle2, Sparkles, TrendingUp, ShieldCheck, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface Plan {
  id: number;
  name: string;
  amount: number;
  interval: string;
  description: string | null;
  maxUsers: number;
  maxInstances: number;
  maxContacts: number;
  isAiEnabled: boolean | null;
  isFlowBuilderEnabled: boolean | null;
}

interface PricingSectionProps {
  plans: Plan[];
  translations?: {
    title?: string;
    subtitle?: string;
    most_popular?: string;
    get_started?: string;
    interval_month?: string;
    interval_year?: string;
  };
}

export function PricingSection({ plans, translations }: PricingSectionProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  // If plans is empty, fallback to default plans
  const displayPlans = plans.length > 0 ? plans : [
    {
      id: 1,
      name: 'Starter Plan',
      amount: 2900,
      interval: 'month',
      description: 'Ideal for solo entrepreneurs and boutique teams getting started.',
      maxUsers: 3,
      maxInstances: 1,
      maxContacts: 5000,
      isAiEnabled: true,
      isFlowBuilderEnabled: false,
    },
    {
      id: 2,
      name: 'Professional Plan',
      amount: 7900,
      interval: 'month',
      description: 'The complete omnichannel automation engine for growing companies.',
      maxUsers: 10,
      maxInstances: 5,
      maxContacts: 50000,
      isAiEnabled: true,
      isFlowBuilderEnabled: true,
    },
    {
      id: 3,
      name: 'Enterprise Scale',
      amount: 19900,
      interval: 'month',
      description: 'Unlimited throughput, custom AI grounding, and dedicated support.',
      maxUsers: 50,
      maxInstances: 20,
      maxContacts: 500000,
      isAiEnabled: true,
      isFlowBuilderEnabled: true,
    },
  ];

  return (
    <section id="pricing" className="py-24 bg-muted/20 border-t border-border/40 scroll-mt-20 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-emerald-500/5 dark:bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        {/* Section Header */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1.5 rounded-full text-xs font-semibold border border-primary/25 bg-primary/10 text-primary shadow-xs">
            <TrendingUp className="size-3.5" />
            <span>Simple, Transparent Pricing</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight text-foreground mb-4">
            {translations?.title || 'Simple pricing that scales with your growth'}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            {translations?.subtitle ||
              'Start with a free 7-day trial. Upgrade, downgrade, or cancel anytime with zero lock-in.'}
          </p>

          {/* Billing Cycle Switcher */}
          <div className="mt-8 inline-flex items-center p-1 rounded-2xl bg-muted/80 border border-border/70 backdrop-blur-xs shadow-inner">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                billingCycle === 'monthly'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly Billing
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                billingCycle === 'annual'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>Annual Billing</span>
              <span className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[10px] font-mono px-2 py-0.5 rounded-full font-bold">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {displayPlans.map((plan, index) => {
            const isPopular = index === 1;
            const baseAmount = plan.amount / 100;
            const finalAmount =
              billingCycle === 'annual'
                ? Math.round(baseAmount * 0.8)
                : baseAmount;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col p-8 rounded-3xl border transition-all duration-300 hover:shadow-2xl ${
                  isPopular
                    ? 'border-emerald-500 bg-card/90 shadow-2xl shadow-emerald-500/10 ring-2 ring-emerald-500/40 md:-translate-y-2 z-10'
                    : 'border-border/80 bg-card/60 backdrop-blur-sm hover:border-border hover:-translate-y-1'
                }`}
              >
                {/* Popular Ribbon */}
                {isPopular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-600 text-white text-[11px] font-black px-4 py-1 rounded-full shadow-lg uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles className="size-3" />
                    <span>{translations?.most_popular || 'MOST POPULAR'}</span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-foreground mb-1.5 flex items-center gap-2">
                    {plan.name}
                  </h3>
                  <p className="text-xs text-muted-foreground min-h-[36px] leading-relaxed">
                    {plan.description || 'Full-featured omnichannel CRM solution.'}
                  </p>
                </div>

                {/* Price Display */}
                <div className="flex items-baseline gap-1.5 mb-6 pb-6 border-b border-border/60">
                  <span className="text-4xl sm:text-5xl font-black font-mono text-foreground tracking-tight">
                    ${finalAmount}
                  </span>
                  <span className="text-xs text-muted-foreground font-medium">
                    /month {billingCycle === 'annual' && '(billed annually)'}
                  </span>
                </div>

                {/* Features List */}
                <ul className="space-y-3.5 mb-8 flex-1 text-xs sm:text-sm">
                  <li className="flex items-center gap-2.5 text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                    <span>Up to {plan.maxUsers} Team Members</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                    <span>{plan.maxInstances} Connected WhatsApp &amp; Social Numbers</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                    <span>{plan.maxContacts.toLocaleString()} Contacts &amp; Leads</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                    <span>WhatsApp, Instagram DMs &amp; Messenger</span>
                  </li>
                  {plan.isAiEnabled && (
                    <li className="flex items-center gap-2.5 text-foreground font-medium">
                      <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        24/7 Autonomous AI Agent Included
                      </span>
                    </li>
                  )}
                  {plan.isFlowBuilderEnabled && (
                    <li className="flex items-center gap-2.5 text-foreground font-medium">
                      <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                      <span className="font-semibold text-primary">
                        Visual Drag-and-Drop Flow Builder
                      </span>
                    </li>
                  )}
                  <li className="flex items-center gap-2.5 text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                    <span>Interactive Product Catalogs &amp; Payments</span>
                  </li>
                </ul>

                {/* CTA Button */}
                <Link href={`/sign-up?priceId=${plan.id}`}>
                  <Button
                    size="lg"
                    className={`w-full rounded-xl h-12 text-sm font-bold transition-all cursor-pointer gap-2 ${
                      isPopular
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40'
                        : 'bg-secondary hover:bg-secondary/80 text-foreground border border-border/80'
                    }`}
                  >
                    <span>{translations?.get_started || 'Start 7-Day Free Trial'}</span>
                    <ArrowRight className="size-4" />
                  </Button>
                </Link>
                <p className="text-[11px] text-center text-muted-foreground mt-2.5">
                  No credit card required to start
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom Trust Guarantee Strip */}
        <div className="mt-14 p-6 rounded-2xl bg-card/60 border border-border/70 backdrop-blur-sm flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
              <ShieldCheck className="size-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">
                100% Risk-Free 14-Day Money-Back Guarantee
              </p>
              <p className="text-xs text-muted-foreground">
                If Zeric doesn&apos;t save your team at least 15 hours per week, we&apos;ll refund your subscription in full.
              </p>
            </div>
          </div>
          <Link href="/contact">
            <Button variant="outline" size="sm" className="rounded-xl text-xs font-semibold whitespace-nowrap">
              Need custom enterprise volume? Contact Sales →
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
